import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Amplify, Auth, Hub } from "aws-amplify";
import { jwtDecode } from "jwt-decode";
import QRCodeSVG from "qrcode.react";
import config from "../../aws-exports";
import "../../styles/login.css";
import { Button, TextField, IconButton, InputAdornment } from "@mui/material";
import { fixUserMFADeliveryConfig, resetUserMFA } from "../../utils/mfaUtils";
import { fetchData } from "../../services/BackendServices";
import { useLoading } from "../../context/LoadingContext";
import AlertModal from "../../pages/Requirement/components/common/AlerModel";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import OfflineShareIcon from "@mui/icons-material/OfflineShare";
import StorefrontIcon from "@mui/icons-material/Storefront";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LeftSideContent from "./LeftSideContent.json";

/* console.log(
  "OAuth Config - Domain:",
  process.env.REACT_APP_AMTPL_SSO_DOMAIN || config.oauth?.domain
); */
/* console.log("OAuth Config - RedirectSignIn:", window.location.origin + "/"); */
/* console.log("OAuth Config - ClientId:", config.aws_user_pools_web_client_id); */

const roleGroupMap = {
  agropreneur: "AMTPL_AGROPRENEURS",
  "global-partner": "AMTPL_GLOBAL_PARTNERS",
  "finance-partner": "AMTPL_FINANCE_PARTNERS",
  "seller-farmer": "AMTPL_SELLERS_AND_FARMERS",
  buyer: "AMTPL_BUYERS",
  amtpl: "AMTPL_ADMINS",
};

const Login = ({ role }) => {
  /* console.log(" Login component initialized with role:", role); */
  /* console.log(" Expected group for this role:", roleGroupMap[role]); */
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { isLoading, setLoading } = useLoading();
  const [userObj, setUserObj] = useState(null);
  const [challengeName, setChallengeName] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [showQRCode, setShowQRCode] = useState(false);
  const [qrCode, setQRCode] = useState("");
  const [username, setUsername] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTotpCode, setShowTotpCode] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [roleTrading, setRoleTrading] = useState("");
  const navigate = useNavigate();
  const iconMap = {
    AttachMoneyIcon: <AttachMoneyIcon />,
    OfflineShareIcon: <OfflineShareIcon />,
    StorefrontIcon: <StorefrontIcon />,
  };

  // Modal state for alerts
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    autoClose: false,
  });

  // Handle modal close
  const handleModalClose = () => {
    setModal((prev) => ({ ...prev, show: false }));
  };

  // Helper function to show modal
  const showAlertModal = (message, type = "error", title = "Login Message") => {
    setModal({
      show: true,
      type: type,
      title: title,
      message: message,
      autoClose: false,
    });
  };

  // Toggle password visibility
  const handleTogglePassword = () => setShowPassword((prev) => !prev);
  const handleToggleNewPassword = () => setShowNewPassword((prev) => !prev);
  const handleToggleConfirmPassword = () =>
    setShowConfirmPassword((prev) => !prev);

  useEffect(() => {
    setRoleTrading(role);
    // Check if user is already authenticated (useful after OAuth redirect)
    const checkAuthStatus = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser({
          bypassCache: false,
        });
        if (user && user.signInUserSession && user.signInUserSession.idToken) {
          /* console.log(" User already authenticated, checking groups..."); */
          const idToken = user.signInUserSession.idToken.jwtToken;
          const decodedToken = jwtDecode(idToken);
          const userGroups = decodedToken["cognito:groups"] || [];
          if (userGroups.includes(roleGroupMap[role])) {
            /* console.log(
              "1. User has required role, navigating to:",
              role === "global-partner" ? "/kyc" : `/${role}`
            ); */
            setTimeout(async () => {
              handleModalClose();
              if (
                role === "global-partner" ||
                role === "buyer" ||
                role === "seller-farmer"
              ) {
                setLoading(true);
                const response = await fetchData({
                  target: "checkUserExist",
                  Input: {
                    Id: decodedToken.sub,
                  },
                });
                let userData = {
                  given_name: decodedToken.given_name,
                  family_name: decodedToken.family_name,
                  email: decodedToken.email,
                  Id: decodedToken.sub,
                  role: role,
                };
                setLoading(false);
                if (response.exists) {
                  navigate(`/${role}`);
                } else {
                  navigate(`/kyc`, { state: { userData } });
                }
              } else {
                navigate(`/${role}`);
              }
            }, 500);
          } else {
            setLoading(false);
            showAlertModal(
              `You have not been assigned the required roles to access. Current groups: ${userGroups.join(
                ", "
              )}. Required: ${
                roleGroupMap[role]
              }. Please contact the administrator.`
            );
            /* console.log("User doesn't have required role for this page"); */
          }
        }
      } catch (error) {
        setLoading(false);
        if (
          error.message &&
          !error.message.includes("No current user") &&
          !error.message.includes("not authenticated")
        ) {
          console.error("Unexpected auth check error:", error);
        } else {
          /* console.log("User not authenticated, staying on login page"); */
        }
      }
    };
    checkAuthStatus();
    Hub.listen("auth", ({ payload: { event, data } }) => {
      /* console.log(" Auth Hub event:", event, data); */
      switch (event) {
        case "signIn":
        case "cognitoHostedUI":
          setLoading(false);
          Auth.currentSession()
            .then((userToken) => {
              try {
                if (!userToken || !userToken.getIdToken()) {
                  console.error("No valid token found in session");
                  showAlertModal(
                    "Invalid session. Please try logging in again."
                  );
                  return;
                }
                const idToken = userToken.getIdToken().getJwtToken();
                const decodedToken = jwtDecode(idToken);
                const userGroups = decodedToken["cognito:groups"] || [];
                if (userGroups.includes(roleGroupMap[role])) {
                  setTimeout(async () => {
                    handleModalClose();
                    if (
                      role === "global-partner" ||
                      role === "buyer" ||
                      role === "seller-farmer"
                    ) {
                      setLoading(true);
                      const response = await fetchData({
                        target: "checkUserExist",
                        Input: {
                          Id: decodedToken.sub,
                        },
                      });
                      /* console.log("Check User Exist Response:", response); */
                      let userData = {
                        given_name: decodedToken.given_name,
                        family_name: decodedToken.family_name,
                        email: decodedToken.email,
                        Id: decodedToken.sub,
                        role: role,
                      };
                      if (response.exists) {
                        navigate(`/${role}`);
                      } else {
                        navigate(`/kyc`, { state: { userData } });
                      }
                      setLoading(false);
                    } else {
                      navigate(`/${role}`);
                    }
                  }, 500);
                } else {
                  setLoading(false);
                  showAlertModal(
                    `You have not been assigned the required roles to access. Current groups: ${userGroups.join(
                      ", "
                    )}. Required: ${
                      roleGroupMap[role]
                    }. Please contact the administrator.`
                  );
                }
              } catch (tokenError) {
                setLoading(false);
                console.error("Error processing token:", tokenError);
                showAlertModal(
                  "Error processing authentication token. Please try logging in again."
                );
              }
            })
            .catch((sessionError) => {
              setLoading(false);
              console.error("Error getting current session:", sessionError);
              showAlertModal("Session error. Please try logging in again.");
            });
          break;
        case "signOut":
          setLoading(false);
          break;
        case "signIn_failure":
        case "cognitoHostedUI_failure":
          setLoading(false);
          console.error(" OAuth login failed:", data);
          showAlertModal(data.message || "Login failed. Please try again.");
          break;
        default:
          break;
      }
    });
  }, [navigate, role, setLoading]);

  const handleLogin = async (event) => {
    event.preventDefault();
    if (email.trim() === "" || password.trim() === "") return;
    setLoading(true);
    try {
      const user = await Auth.signIn(email, password);
      /* console.log("Auth.signIn result:", user); */
      /* console.log("User challenge name:", user.challengeName); */
      /* console.log("User signInUserSession:", user.signInUserSession); */
      setUserObj(user);
      setChallengeName(user.challengeName);
      if (user.challengeName === "MFA_SETUP") {
        setShowTotpCode(true);
        const code = await Auth.setupTOTP(user);
        setQRCode(code);
        setUsername(user.username);
        setShowQRCode(true);
      } else if (user.challengeName === "SOFTWARE_TOKEN_MFA") {
        setShowQRCode(false);
        setShowTotpCode(true);
      } else if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        /* console.log(" User must change password before proceeding"); */
        setShowPasswordChange(true);
        setShowTotpCode(false);
        setShowQRCode(false);
      } else {
        try {
          if (!user.signInUserSession || !user.signInUserSession.idToken) {
            /* console.log(
              "No valid session found, waiting for session to be established..."
            ); */
            const currentUser = await Auth.currentAuthenticatedUser();
            if (
              currentUser &&
              currentUser.signInUserSession &&
              currentUser.signInUserSession.idToken
            ) {
              const idToken = currentUser.signInUserSession.idToken.jwtToken;
              const decodedToken = jwtDecode(idToken);
              const userGroups = decodedToken["cognito:groups"] || [];
              /* console.log("User groups after login (no MFA):", userGroups); */
              /* console.log("Required role:", roleGroupMap[role]); */
              /* console.log("Current role:", role); */
              if (userGroups.includes(roleGroupMap[role])) {
                /* console.log("Current role:", role); */
                if (
                  role === "global-partner" ||
                  role === "buyer" ||
                  role === "seller-farmer"
                ) {
                  let userData = {
                    given_name: decodedToken.given_name,
                    family_name: decodedToken.family_name,
                    email: decodedToken.email,
                    Id: decodedToken.sub,
                    role: role,
                  };
                  navigate(`/kyc`, { state: { userData } });
                } else {
                  navigate(`/${role}`);
                }
              } else {
                showAlertModal(
                  `You have not been assigned the required roles to access. Current groups: ${userGroups.join(
                    ", "
                  )}. Required: ${
                    roleGroupMap[role]
                  }. Please contact the administrator.`
                );
              }
            } else {
              showAlertModal(
                "Session not properly established. Please try logging in again."
              );
            }
          } else {
            const idToken = user.signInUserSession.idToken.jwtToken;
            const decodedToken = jwtDecode(idToken);
            const userGroups = decodedToken["cognito:groups"] || [];
            if (userGroups.includes(roleGroupMap[role])) {
              /* console.log("Current role:", role); */
              if (
                role === "global-partner" ||
                role === "buyer" ||
                role === "seller-farmer"
              ) {
                let userData = {
                  given_name: decodedToken.given_name,
                  family_name: decodedToken.family_name,
                  email: decodedToken.email,
                  Id: decodedToken.sub,
                  role: role,
                };
                navigate(`/kyc`, { state: { userData } });
              } else {
                navigate(`/${role}`);
              }
            } else {
              showAlertModal(
                `You have not been assigned the required roles to access. Current groups: ${userGroups.join(
                  ", "
                )}. Required: ${
                  roleGroupMap[role]
                }. Please contact the administrator.`
              );
            }
          }
        } catch (sessionError) {
          console.error("Error accessing user session:", sessionError);
          showAlertModal(
            "Error accessing user session. Please try logging in again."
          );
        }
      }
    } catch (err) {
      const errorMessage =
        err.message === "User is disabled."
          ? "Oops! Something went wrong. Please reach out to your administrator for help."
          : err.message;
      showAlertModal(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (event) => {
    event.preventDefault();
    try {
      /* console.log(challengeName); */
      if (challengeName === "MFA_SETUP") {
        try {
          await Auth.verifyTotpToken(userObj, totpCode);
          await Auth.setPreferredMFA(userObj, "TOTP");
        } catch (mfaError) {
          console.error("MFA Setup Error:", mfaError);
          if (
            mfaError.code === "InvalidParameterException" &&
            mfaError.message.includes(
              "delivery config set to turn on SOFTWARE_TOKEN_MFA"
            )
          ) {
            /* console.log("Attempting to fix MFA delivery configuration..."); */
            try {
              const fixResult = await fixUserMFADeliveryConfig(
                userObj.username
              );
              if (fixResult.success) {
                if (fixResult.qrCode) {
                  setQRCode(fixResult.qrCode);
                  showAlertModal(
                    "MFA configuration updated. Please scan the new QR code and try again.",
                    "success",
                    "MFA Configuration Updated"
                  );
                } else {
                  showAlertModal(
                    "MFA delivery configuration fixed. Please try entering your TOTP code again.",
                    "success",
                    "MFA Fixed"
                  );
                }
                return;
              } else {
                showAlertModal(
                  `Failed to fix MFA configuration: ${fixResult.error}`,
                  "error",
                  "MFA Configuration Failed"
                );
                return;
              }
            } catch (fixError) {
              console.error("Failed to fix MFA delivery config:", fixError);
              showAlertModal(
                "Failed to configure MFA. Please contact administrator."
              );
              return;
            }
          } else {
            throw mfaError;
          }
        }
        try {
          const user = await Auth.currentAuthenticatedUser({
            bypassCache: true,
          });
          if (
            !user ||
            !user.signInUserSession ||
            !user.signInUserSession.idToken
          ) {
            console.error("No valid session found after MFA setup");
            showAlertModal(
              "Session not properly established after MFA setup. Please try logging in again."
            );
            return;
          }
          const idToken = user.signInUserSession.idToken.jwtToken;
          const decodedToken = jwtDecode(idToken);
          const userGroups = decodedToken["cognito:groups"] || [];
          /* console.log("User groups after MFA setup:", userGroups); */
          /* console.log("Required role:", roleGroupMap[role]); */
          /* console.log("Current role:", role); */
          /* console.log(
            "Will navigate to:",
            role === "global-partner" ? "/kyc" : `/${role}`
          ); */
          /* console.log(
            "Group check result:",
            userGroups.includes(roleGroupMap[role])
          ); */
          if (userGroups.includes(roleGroupMap[role])) {
            /* console.log(
              "2. User has required role, navigating to:",
              role === "global-partner" ? "/kyc" : `/${role}`
            ); */
            if (
              role === "global-partner" ||
              role === "buyer" ||
              role === "seller-farmer"
            ) {
              showAlertModal("Login successful", "success", "Login Successful");
              let userData = {
                given_name: decodedToken.given_name,
                family_name: decodedToken.family_name,
                email: decodedToken.email,
                Id: decodedToken.sub,
                role: role,
              };
              navigate(`/kyc`, { state: { userData } });
            } else {
              navigate(`/${role}`);
            }
          } else {
            showAlertModal(
              `You have not been assigned the required roles to access. Current groups: ${userGroups.join(
                ", "
              )}. Required: ${
                roleGroupMap[role]
              }. Please contact the administrator.`
            );
          }
        } catch (sessionError) {
          console.error(
            "Error accessing user session after MFA setup:",
            sessionError
          );
          showAlertModal(
            "Error accessing user session after MFA setup. Please try logging in again."
          );
        }
      } else if (challengeName === "SOFTWARE_TOKEN_MFA") {
        await Auth.confirmSignIn(userObj, totpCode, "SOFTWARE_TOKEN_MFA");
        try {
          const user = await Auth.currentAuthenticatedUser({
            bypassCache: true,
          });
          if (
            !user ||
            !user.signInUserSession ||
            !user.signInUserSession.idToken
          ) {
            console.error("No valid session found after TOTP confirmation");
            showAlertModal(
              "Session not properly established after TOTP confirmation. Please try logging in again."
            );
            return;
          }
          const idToken = user.signInUserSession.idToken.jwtToken;
          const decodedToken = jwtDecode(idToken);
          const userGroups = decodedToken["cognito:groups"] || [];
          /* console.log("User groups after TOTP confirmation:", userGroups); */
          /* console.log("Required role:", roleGroupMap[role]); */
          /* console.log("Current role:", role); */
          /* console.log(
            "Will navigate to:",
            role === "global-partner" ? "/kyc" : `/${role}`
          ); */
          /* console.log(
            "Group check result:",
            userGroups.includes(roleGroupMap[role])
          ); */
          if (userGroups.includes(roleGroupMap[role])) {
            /* console.log(
              "3. User has required role, navigating to:",
              role === "global-partner" ? "/kyc" : `/${role}`
            ); */
            if (
              role === "global-partner" ||
              role === "buyer" ||
              role === "seller-farmer"
            ) {
              let userData = {
                given_name: decodedToken.given_name,
                family_name: decodedToken.family_name,
                email: decodedToken.email,
                Id: decodedToken.sub,
                role: role,
              };
              navigate(`/kyc`, { state: { userData } });
            } else {
              navigate(`/${role}`);
            }
          } else {
            showAlertModal(
              `You have not been assigned the required roles to access. Current groups: ${userGroups.join(
                ", "
              )}. Required: ${
                roleGroupMap[role]
              }. Please contact the administrator.`
            );
          }
        } catch (sessionError) {
          console.error(
            "Error accessing user session after TOTP confirmation:",
            sessionError
          );
          showAlertModal(
            "Error accessing user session after TOTP confirmation. Please try logging in again."
          );
        }
      }
    } catch (err) {
      console.error("TOTP Submit Error:", err);
      showAlertModal(err.message);
    }
  };

  const handleNewPasswordSubmit = async (event) => {
    event.preventDefault();
    if (!newPassword.trim()) {
      showAlertModal("New password is required");
      return;
    }
    if (newPassword !== confirmPassword) {
      showAlertModal("Passwords do not match");
      return;
    }
    const minLength = 8;
    const hasUppercase = /[A-Z]/.test(newPassword);
    const hasLowercase = /[a-z]/.test(newPassword);
    const hasNumbers = /\d/.test(newPassword);
    const hasSymbols = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
    if (
      newPassword.length < minLength ||
      !hasUppercase ||
      !hasLowercase ||
      !hasNumbers ||
      !hasSymbols
    ) {
      showAlertModal(
        "Password must be at least 8 characters with uppercase, lowercase, number, and special character"
      );
      return;
    }
    setLoading(true);
    try {
      const updatedUser = await Auth.completeNewPassword(userObj, newPassword);
      /* console.log("Password updated successfully:", updatedUser); */
      if (
        updatedUser &&
        updatedUser.signInUserSession &&
        updatedUser.signInUserSession.idToken
      ) {
        const idToken = updatedUser.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(idToken);
        const userGroups = decodedToken["cognito:groups"] || [];
        /* console.log("User groups after password change:", userGroups); */
        /* console.log("Required role:", roleGroupMap[role]); */
        if (userGroups.includes(roleGroupMap[role])) {
          showAlertModal(
            "Password updated successfully! Redirecting...",
            "success",
            "Success"
          );
          setTimeout(() => {
            handleModalClose();
            navigate(`/${role}`);
          }, 1500);
        } else {
          showAlertModal(
            `You have not been assigned the required roles to access. Current groups: ${userGroups.join(
              ", "
            )}. Required: ${
              roleGroupMap[role]
            }. Please contact the administrator.`
          );
        }
      } else {
        showAlertModal(
          "Password updated successfully! Please log in again.",
          "success",
          "Success"
        );
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (err) {
      console.error("Error updating password:", err);
      showAlertModal(
        err.message || "Failed to update password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };
  ///* console.log('process.env.REACT_APP_AMTPL_SSO_DOMAIN', process.env.REACT_APP_AMTPL_SSO_DOMAIN);

  const googleLogin = async () => {
    try {
      setLoading(true);
      /* console.log("Initiating Google OAuth login for role:", role); */
      localStorage.setItem("pendingOAuthRole", role);
      /* console.log("Stored role in localStorage:", role); */
      Auth.configure({
        oauth: {
          domain:
            process.env.REACT_APP_AMTPL_SSO_DOMAIN ||
            config.oauth?.domain ||
            "auth-devlab.grainsglobaldev.com",
          scope: [
            "email",
            "phone",
            "openid",
            "aws.cognito.signin.user.admin",
            "profile",
          ],
          redirectSignIn: window.location.origin + "/",
          redirectSignOut: window.location.origin + "/",
          responseType: "code",
        },
      });
      /* console.log(
        "OAuth configured with domain:",
        process.env.REACT_APP_AMTPL_SSO_DOMAIN || config.oauth?.domain
      ); */
      /* console.log("OAuth redirectSignIn:", window.location.origin + "/"); */
      const oauthDomain =
        process.env.REACT_APP_AMTPL_SSO_DOMAIN ||
        config.oauth?.domain ||
        "auth-devlab.grainsglobaldev.com";
      const clientId = config.aws_user_pools_web_client_id;
      const redirectUri = encodeURIComponent(window.location.origin + "/");
      const scope = encodeURIComponent(
        "email phone openid aws.cognito.signin.user.admin profile"
      );
      const state = encodeURIComponent(JSON.stringify({ role }));
      const googleAuthUrl =
        `https://${oauthDomain}/oauth2/authorize?` +
        `identity_provider=Google&` +
        `redirect_uri=${redirectUri}&` +
        `response_type=code&` +
        `client_id=${clientId}&` +
        `scope=${scope}&` +
        `state=${state}&` +
        `prompt=select_account`;
      /* console.log("Redirecting to Google OAuth with prompt=select_account"); */
      window.location.href = googleAuthUrl;
    } catch (error) {
      console.error("Google login error:", error);
      localStorage.removeItem("pendingOAuthRole");
      showAlertModal(
        `Failed to initiate Google login: ${
          error.message || "Please try again."
        }`
      );
      setLoading(false);
    }
  };

  const roleContent =
    LeftSideContent[role] || LeftSideContent["global-partner"];

  const linkedinLogin = async () => {
    try {
      setLoading(true);
      /* console.log("Initiating LinkedIn OAuth login for role:", role); */
      localStorage.setItem("pendingOAuthRole", role);
      /* console.log("Stored role in localStorage:", role); */

      // LinkedIn through Cognito OIDC only supports: openid, profile, email
      // Don't include phone or aws.cognito.signin.user.admin for LinkedIn
      const oauthDomain =
        process.env.REACT_APP_AMTPL_SSO_DOMAIN ||
        config.oauth?.domain ||
        "auth-devlab.grainsglobaldev.com";
      const clientId = config.aws_user_pools_web_client_id;
      const redirectUri = encodeURIComponent(window.location.origin + "/");

      // Use only LinkedIn-compatible scopes
      const scope = encodeURIComponent("openid profile email");
      const state = encodeURIComponent(JSON.stringify({ role }));

      const linkedinAuthUrl =
        `https://${oauthDomain}/oauth2/authorize?` +
        `identity_provider=LinkedIn&` +
        `redirect_uri=${redirectUri}&` +
        `response_type=code&` +
        `client_id=${clientId}&` +
        `scope=${scope}&` +
        `state=${state}`;

      /* console.log("Redirecting to LinkedIn OAuth"); */
      console.log("LinkedIn Auth URL:", linkedinAuthUrl);
      window.location.href = linkedinAuthUrl;
    } catch (error) {
      console.error("LinkedIn login error:", error);
      localStorage.removeItem("pendingOAuthRole");
      showAlertModal(
        `Failed to initiate LinkedIn login: ${
          error.message || "Please try again."
        }`
      );
      setLoading(false);
    }
  };

  return (
    <div className="main-login-page">
      <div className="login-page">
        <div className="login-page-left">
          <div className="content-log">
            <h2
              style={{
                color: "white",
                fontSize: "30px",
                fontWeight: "bold",
                marginBottom: "15px",
                lineHeight: "1.2",
              }}
            >
              {roleContent.title}
            </h2>
            <p
              style={{
                color: "white",
                fontSize: "14px",
                marginBottom: "15px",
                lineHeight: "1.4",
              }}
            >
              {roleContent.description}
            </p>
            <div style={{ marginBottom: "20px" }}>
              <h3
                style={{
                  color: "white",
                  fontSize: "1.1rem",
                  fontWeight: "600",
                  marginBottom: "10px",
                }}
              >
                Key Benefits:
              </h3>
              {roleContent.benefits.map((benefit, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginBottom: "10px",
                    color: "white",
                  }}
                >
                  <span
                    style={{
                      fontSize: "1.2rem",
                      marginRight: "10px",
                      marginTop: "2px",
                    }}
                  >
                    {iconMap[benefit.icon]}
                  </span>
                  <div>
                    <strong style={{ fontSize: "1rem" }}>
                      {benefit.title}:
                    </strong>
                    <br />
                    <span style={{ fontSize: "0.8rem", fontWeight: "500" }}>
                      {benefit.description}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: "left" }}>
              <Link
                target="_blank"
                to={roleContent.cta.link}
                style={{
                  color: "white",
                  fontSize: "0.8rem",
                  fontWeight: "500",
                  cursor: "pointer",
                  textDecoration: "underline",
                  transition: "color 0.3s",
                }}
              >
                {roleContent.cta.text} →
              </Link>
            </div>
          </div>
        </div>
        <div className="form-section">
          <form onSubmit={handleLogin}>
            <div
              className="form-under"
              style={{ overflowY: "auto", maxHeight: "700" }}
            >
              <div>
                <div className="text-center">
                  <img
                    src="/images/logo.png"
                    alt="Grains Global Logo"
                    className="logo"
                    style={{ width: "150px", marginBottom: "0px" }}
                  />
                </div>
                <h1>Welcome to Grains Global</h1>
                {(role === "global-partner" ||
                  role === "buyer" ||
                  role === "seller-farmer") &&
                  !showPasswordChange && (
                    <div className="display-flex-login">
                      <div className="subtext">
                        Sign in to your account or get started.
                      </div>
                      <div className="g-sec">
                        <button
                          className="btn social-btn mb-1"
                          onClick={googleLogin}
                          disabled={isLoading}
                        >
                          <img
                            src="https://www.svgrepo.com/show/355037/google.svg"
                            width="20"
                            className="me-2"
                            alt="Google"
                          />
                          {isLoading ? "Signing in..." : "Sign in with Google"}
                        </button>
                        <button
                          className="btn social-btn mb-1"
                          onClick={linkedinLogin}
                          disabled={isLoading}
                        >
                          <img
                            src="https://www.svgrepo.com/show/448234/linkedin.svg"
                            width="20"
                            className="me-2"
                            alt="LinkedIn"
                          />
                          {isLoading
                            ? "Signing in..."
                            : "Sign in with LinkedIn"}
                        </button>
                        <div className="divider">or continue with</div>
                      </div>
                    </div>
                  )}
                {role === "amtpl" && !showPasswordChange && (
                  <p
                    className="text-muted"
                    style={{
                      fontSize: "0.9rem",
                      textAlign: "left",
                    }}
                  >
                    Please enter your credentials and TOTP code for secure
                    login.
                  </p>
                )}
                {showPasswordChange && (
                  <p
                    className="text-muted"
                    style={{
                      fontSize: "0.9rem",
                    }}
                  >
                    You must change your password before continuing. Enter your
                    new password below.
                  </p>
                )}
              </div>
              {!showPasswordChange && (
                <>
                  <div>
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      className="form-control"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="password">Password</label>
                    <TextField
                      type={showPassword ? "text" : "password"}
                      id="password"
                      className="form-control"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      variant="outlined"
                      size="small"
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleTogglePassword}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </>
              )}
              {showPasswordChange && (
                <>
                  <div>
                    <label htmlFor="newPassword">New Password</label>
                    <TextField
                      type={showNewPassword ? "text" : "password"}
                      id="newPassword"
                      className="form-control"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      variant="outlined"
                      size="small"
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle new password visibility"
                              onClick={handleToggleNewPassword}
                              edge="end"
                            >
                              {showNewPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                  <div>
                    <label htmlFor="confirmPassword">
                      Confirm New Password
                    </label>
                    <TextField
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      className="form-control"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      variant="outlined"
                      size="small"
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle confirm password visibility"
                              onClick={handleToggleConfirmPassword}
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: "10px" }}>
                    <small className="text-muted">
                      Password requirements:
                      <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
                        <li>At least 8 characters</li>
                        <li>One uppercase letter</li>
                        <li>One lowercase letter</li>
                        <li>One number</li>
                        <li>One special character</li>
                      </ul>
                    </small>
                  </div>
                </>
              )}
              {showQRCode === true && (
                <>
                  <div className="form-group text-center">
                    <label htmlFor="exampleInputPassword">
                      TOTP - setup to get authentication code
                    </label>
                    <div>
                      <h5>setup instructions</h5>
                      <ul
                        className={`totp-steps mb-1 ${
                          isExpanded ? "expanded" : ""
                        }`}
                      >
                        {isExpanded ? (
                          <>
                            <li>
                              Download & Install the Microsoft/Google
                              Authenticator app from Play Store for Android
                              users and from App Store for iOS users.
                            </li>
                            <li>
                              To scan the QR code, click on the "+" icon in the
                              app and select the option "work or school
                              account".
                            </li>
                            <li>
                              Click on the "Scan a QR code" option and scan the
                              QR code displayed on the platform.
                            </li>
                            <li>An AWSCognito account will be created.</li>
                            <li>
                              Click on the account to get an authentication
                              code.
                            </li>
                            <li>
                              Enter the authentication code in the platform and
                              click on the submit button.
                            </li>
                          </>
                        ) : (
                          <li>
                            Download & Install the Microsoft Authenticator app
                            from Play Store for Android users and from App Store
                            for iOS users...
                          </li>
                        )}
                      </ul>
                      <button
                        className="btn p-0 auth-link text-green"
                        onClick={(event) => {
                          event.preventDefault();
                          setIsExpanded(!isExpanded);
                        }}
                      >
                        {isExpanded ? "show less" : "show more"}
                      </button>
                    </div>
                    <div className="input-group d-flex justify-content-center">
                      <QRCodeSVG
                        size={256}
                        value={`otpauth://totp/AWSCognito:${username}?secret=${qrCode}&issuer=${process.env.REACT_APP_MFANAME}`}
                        includeMargin={true}
                      />
                    </div>
                  </div>
                </>
              )}
              {(showQRCode === true || showTotpCode === true) && (
                <div>
                  <label htmlFor="totpCode">Enter TOTP Code</label>
                  <input
                    type="text"
                    id="totpCode"
                    className="form-control"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    required
                  />
                </div>
              )}
              <div className="mb-0">
                {showTotpCode === false && showPasswordChange === false && (
                  <div>
                    <div style={{ marginTop: "5px" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <></>
                        {role !== "finance-partner" ? (
                          <Link
                            to={`/forgot-password?role=${role}`}
                            style={{
                              color: "#de6b14",
                              textDecoration: "none",
                              fontSize: "0.7rem",
                            }}
                          >
                            Forgot your password?
                          </Link>
                        ) : (
                          <span
                            style={{
                              color: "rgba(224, 100, 4, 1)",
                              textDecoration: "none",
                              fontSize: "0.7rem",
                              cursor: "not-allowed",
                            }}
                          >
                            Forgot your password?
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "#de6b14",
                        border: "1px solid #de6b14",
                        fontWeight: "500",
                        color: "white",
                        "&:hover": {
                          border: "1px solid #de6b14",
                          backgroundColor: "#fff",
                          color: "#de6b14",
                          fontWeight: "500",
                        },
                      }}
                      type="submit"
                      disabled={isLoading}
                    >
                      Login
                    </Button>
                  </div>
                )}
                {showPasswordChange && (
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#de6b14",
                      color: "white",
                      "&:hover": {
                        border: "1px solid #de6b14",
                        backgroundColor: "#fff",
                        color: "#de6b14",
                        fontWeight: "500",
                      },
                    }}
                    onClick={handleNewPasswordSubmit}
                    disabled={isLoading}
                  >
                    Update Password
                  </Button>
                )}
                {(showQRCode === true || showTotpCode === true) && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      width: "100%",
                    }}
                  >
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "#de6b14",
                        color: "white",
                        "&:hover": {
                          border: "1px solid #de6b14",
                          backgroundColor: "#fff",
                          color: "#de6b14",
                          fontWeight: "500",
                        },
                      }}
                      onClick={handleTotpSubmit}
                    >
                      Submit
                    </Button>
                    {challengeName === "MFA_SETUP" && (
                      <Button
                        variant="outlined"
                        sx={{
                          borderColor: "#de6b14",
                          color: "#de6b14",
                          fontSize: "0.8rem",
                          "&:hover": {
                            backgroundColor: "#de6b14",
                            color: "white",
                          },
                        }}
                        onClick={async () => {
                          try {
                            const resetResult = await resetUserMFA();
                            if (resetResult.success) {
                              setQRCode(resetResult.qrCode);
                              showAlertModal(
                                "MFA has been reset. Please scan the new QR code with your authenticator app.",
                                "success",
                                "MFA Reset"
                              );
                            } else {
                              showAlertModal(
                                `Failed to reset MFA: ${resetResult.error}`,
                                "error",
                                "MFA Reset Failed"
                              );
                            }
                          } catch (error) {
                            showAlertModal(
                              "Failed to reset MFA. Please contact administrator."
                            );
                          }
                        }}
                      >
                        Reset MFA Configuration
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {role === "amtpl" ? (
                <div></div>
              ) : (
                <>
                  {role === "finance-partner" ? (
                    <></>
                  ) : (
                    <>
                      <div
                        style={{
                          textAlign: "center",
                          fontSize: "13px",
                          color: "#706969",
                          marginTop: "10px",
                        }}
                      >
                        Not yet a{" "}
                        {role
                          .replace("-", " ")
                          .split(" ")
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                          )
                          .join(" ")}
                        ?{" "}
                        <Link
                          target="_blank"
                          to="https://www.grains.global/seller-apply"
                          style={{
                            color: "#de6b14",
                            textDecoration: "none",
                            fontWeight: 500,
                          }}
                        >
                          Apply now
                        </Link>{" "}
                        or{" "}
                        <Link
                          target="_blank"
                          to="https://grains.global/"
                          style={{
                            color: "#de6b14",
                            textDecoration: "none",
                            fontWeight: 500,
                          }}
                        >
                          contact support
                        </Link>
                        .
                      </div>
                    </>
                  )}

                  <div
                    style={{
                      textAlign: "center",
                      fontSize: "10px",
                      marginTop: "15px",
                      color: "rgb(85 85 85 / 56%)",
                      lineHeight: "1.5",
                    }}
                  >
                    By logging in, you agree to our{" "}
                    <Link
                      target="_blank"
                      to="https://grains.global/terms-and-conditions"
                      style={{
                        color: "#de6b14",
                        textDecoration: "none",
                        fontWeight: 500,
                      }}
                    >
                      Terms and Conditions
                    </Link>{" "}
                    and{" "}
                    <Link
                      target="_blank"
                      to="https://grains.global/privacy-policy"
                      style={{
                        color: "#de6b14",
                        textDecoration: "none",
                        fontWeight: 500,
                      }}
                    >
                      Privacy Policy
                    </Link>
                    .
                  </div>
                </>
              )}
            </div>
          </form>
        </div>
        <AlertModal
          type={modal.type}
          title={modal.title}
          message={modal.message}
          showModal={modal.show}
          onClose={handleModalClose}
          buttonText="OK"
          autoClose={modal.autoClose}
        />
      </div>
    </div>
  );
};

export default Login;
